import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── A client's chart of accounts ────────────────────────────────────────────────────────────────
// The Accounting Act does not mandate a chart as such. It is implied: s.7 lets the DBD prescribe
// the books, the 2544 notification requires ledgers that separate assets/liabilities/capital,
// income/expense, debtors and creditors, and s.21(1) requires a Thai code translation manual if
// entries carry codes. The Thai name IS that manual, which is why it cannot be left blank.
//
// An account that has been posted to is frozen in code and class — printed books cite them — but
// its name can always be corrected, and it can be switched off so it stops being offered.

type Account = { id: string; code: string; name_th: string; name_en: string; class: string; control: string; active: number; lines: number; net_dr: number; cf_section: string };

// Label keys only — t() is hook-bound and cannot be called at module scope.
const CLASS_KEY: Record<string, string> = {
  asset: 'pa_chart_class_asset', liability: 'pa_chart_class_liability', equity: 'pa_chart_class_equity',
  income: 'pa_chart_class_income', expense: 'pa_chart_class_expense',
};
const ORDER = ['asset', 'liability', 'equity', 'income', 'expense'];
// Where this account's movement lands on the cash flow statement.
const CF = [
  { k: '', key: 'pa_chart_cf_none' },
  { k: 'operating', key: 'pa_chart_cf_operating' },
  { k: 'investing', key: 'pa_chart_cf_investing' },
  { k: 'financing', key: 'pa_chart_cf_financing' },
  { k: 'cash', key: 'pa_chart_cf_cash' },
  { k: 'noncash', key: 'pa_chart_cf_noncash' },
];
// A balance-sheet account with no section is the one that actually breaks the statement: its
// movement is not inside the profit the indirect method starts from, so leaving it out of every
// total makes the net change miss the real cash movement by exactly that amount. An income or
// expense account without one is only untidy.
const cfMatters = (cls: string) => ['asset', 'liability', 'equity'].includes(cls);
const baht = (s: number) => (Number(s || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '5px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};

export default function ChartOfAccounts({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Account[] | null>(null);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ code: '', nameTh: '', nameEn: '', cls: 'expense', cfSection: '' });
  const [editId, setEditId] = useState('');
  const [edit, setEdit] = useState({ nameTh: '', nameEn: '', cfSection: '' });

  const load = useCallback(async () => {
    try { const d: any = await api.acctChart(clientId); setRows(d.accounts || []); }
    catch (e: any) { setErr(e.message); setRows([]); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    setBusy('copy'); setErr('');
    try { const r: any = await api.acctCopyTemplate(clientId); await load(); if (!r.copied) setErr(t('pa_chart_template_complete')); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const add = async () => {
    setBusy('add'); setErr('');
    try { await api.acctAddAccount(clientId, draft); setDraft({ code: '', nameTh: '', nameEn: '', cls: 'expense', cfSection: '' }); setAdding(false); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const saveEdit = async (id: string) => {
    setBusy(id); setErr('');
    try { await api.acctUpdateAccount(id, edit); setEditId(''); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const toggle = async (a: Account) => {
    setBusy(a.id); setErr('');
    try { await api.acctUpdateAccount(a.id, { active: !a.active }); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const remove = async (a: Account) => {
    if (!window.confirm(t('pa_chart_remove_confirm', { code: a.code, name: a.name_th }))) return;
    setBusy(a.id); setErr('');
    try { await api.acctDeleteAccount(a.id); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  if (rows === null) return null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-list-numbers" style={{ fontSize: 18, color: 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_chart_title')}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {rows.length ? t('pa_chart_counts', { n: rows.length, used: rows.filter(r => r.lines).length }) : t('pa_chart_empty')}
          </span>
        </span>
        {!rows.length && <button onClick={copy} disabled={!!busy} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>
          <i className={busy === 'copy' ? 'ti ti-loader' : 'ti ti-copy'} aria-hidden /> {t('pa_chart_copy_template')}
        </button>}
        {!!rows.length && <button onClick={() => setOpen(o => !o)} style={btn}>{open ? t('pa_chart_hide') : t('pa_chart_open')}</button>}
      </div>

      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {open && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 13px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7, marginBottom: 9 }}>
            <button onClick={copy} disabled={!!busy} style={btn} title={t('pa_chart_sync_title')}>
              <i className="ti ti-copy" aria-hidden /> {t('pa_chart_sync_template')}
            </button>
            <button onClick={() => setAdding(a => !a)} style={{ ...btn, background: adding ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
              <i className="ti ti-plus" aria-hidden /> {t('pa_chart_add_account')}
            </button>
          </div>

          {adding && (
            <div style={{ display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center', padding: 10, borderRadius: 8, background: 'var(--bg-app)' }}>
              <input value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value })} placeholder={t('pa_chart_ph_code')} style={{ ...inp, width: 80 }} />
              <input value={draft.nameTh} onChange={e => setDraft({ ...draft, nameTh: e.target.value })} placeholder={t('pa_chart_ph_name_th')} style={{ ...inp, width: 200 }} />
              <input value={draft.nameEn} onChange={e => setDraft({ ...draft, nameEn: e.target.value })} placeholder={t('pa_chart_ph_name_en')} style={{ ...inp, width: 180 }} />
              <select value={draft.cls} onChange={e => setDraft({ ...draft, cls: e.target.value })} style={inp}>
                {ORDER.map(c => <option key={c} value={c}>{t(CLASS_KEY[c] || c)}</option>)}
              </select>
              <select value={draft.cfSection} onChange={e => setDraft({ ...draft, cfSection: e.target.value })}
                style={{ ...inp, borderColor: cfMatters(draft.cls) && !draft.cfSection ? '#B45309' : (inp.border as string) }}>
                {CF.map(c => <option key={c.k} value={c.k}>{c.k ? t('pa_chart_cf_prefixed', { section: t(c.key) }) : t(c.key)}</option>)}
              </select>
              <button onClick={add} disabled={busy === 'add'} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>{t('pa_chart_save')}</button>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('pa_chart_name_th_manual')}</span>
              {cfMatters(draft.cls) && !draft.cfSection && (
                <span style={{ fontSize: 11, color: '#92400E', width: '100%' }}>
                  {t('pa_chart_cf_missing_warn')}
                </span>
              )}
            </div>
          )}

          {ORDER.filter(c => rows.some(r => r.class === c)).map(cls => (
            <div key={cls} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 5 }}>
                {t(CLASS_KEY[cls] || cls)}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {rows.filter(r => r.class === cls).map(a => (
                    <tr key={a.id} style={{ borderTop: '1px solid var(--border)', opacity: a.active ? 1 : 0.5 }}>
                      <td style={{ padding: '6px 8px', width: 62, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{a.code}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {editId === a.id ? (
                          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <input value={edit.nameTh} onChange={e => setEdit({ ...edit, nameTh: e.target.value })} style={{ ...inp, width: 190 }} />
                            <input value={edit.nameEn} onChange={e => setEdit({ ...edit, nameEn: e.target.value })} style={{ ...inp, width: 170 }} />
                            <select value={edit.cfSection} onChange={e => setEdit({ ...edit, cfSection: e.target.value })} style={inp}>
                              {CF.map(c => <option key={c.k} value={c.k}>{c.k ? t('pa_chart_cf_prefixed', { section: t(c.key) }) : t(c.key)}</option>)}
                            </select>
                            <button onClick={() => saveEdit(a.id)} style={{ ...btn, padding: '4px 9px' }}>{t('pa_chart_save')}</button>
                            <button onClick={() => setEditId('')} style={{ ...btn, padding: '4px 9px' }}>{t('pa_chart_cancel')}</button>
                          </span>
                        ) : (
                          <>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{a.name_th}</span>
                            <span style={{ marginLeft: 8, color: 'var(--text-tertiary)' }}>{a.name_en}</span>
                            {a.control && <span style={{ marginLeft: 7, fontSize: 10, fontWeight: 800, color: '#1D4ED8', background: '#EFF6FF', border: '1px solid #BFDBFE', padding: '0 6px', borderRadius: 999 }}>{a.control.toUpperCase()}</span>}
                            {cfMatters(a.class) && !a.cf_section && (
                              <span title={t('pa_chart_cf_missing_title')} style={{ marginLeft: 7, fontSize: 10, fontWeight: 800, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8', padding: '0 6px', borderRadius: 999 }}>{t('pa_chart_cf_missing_pill')}</span>
                            )}
                          </>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {a.lines ? baht(Math.abs(a.net_dr)) : ''}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', width: 150 }}>
                        {editId !== a.id && (<>
                          <button onClick={() => { setEditId(a.id); setEdit({ nameTh: a.name_th, nameEn: a.name_en, cfSection: a.cf_section || '' }); }} style={{ ...btn, padding: '3px 7px', fontSize: 11 }}>{t('pa_chart_rename')}</button>
                          <button onClick={() => toggle(a)} disabled={busy === a.id} style={{ ...btn, padding: '3px 7px', fontSize: 11, marginLeft: 5 }}>{a.active ? t('pa_chart_switch_off') : t('pa_chart_switch_on')}</button>
                          {/* Only an account nothing has ever been posted to can go: the ledger and
                              the printed books still cite the others. */}
                          {!a.lines && <button onClick={() => remove(a)} disabled={busy === a.id} style={{ ...btn, padding: '3px 7px', fontSize: 11, marginLeft: 5, color: '#B42318' }}>
                            <i className="ti ti-trash" aria-hidden /></button>}
                        </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
